import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { ApiResponse } from "@/types/api/types.ts";
import type { components } from "@/types/api/generated-api-schema.ts";

// Helper function to get CSRF token from cookie
function getCsrfToken(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  
  const name = 'XSRF-TOKEN=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(';');
  
  for (const cookie of cookies) {
    let trimmedCookie = cookie;
    while (trimmedCookie.startsWith(' ')) {
      trimmedCookie = trimmedCookie.substring(1);
    }
    if (trimmedCookie.startsWith(name)) {
      return trimmedCookie.substring(name.length, trimmedCookie.length);
    }
  }
  
  return null;
}

export type PublicStudio = {
  uuid: string;
  number: number;
  name: string;
  building: string;
  floor: number;
  floorLabel: string;
  size: string;
  price: string;
  agencyFees: string | null;
  status: string;
  availableFrom: string | null;
  enabled: boolean;
  studioModelName: string | null;
  studioModelUuid: string | null;
  descriptionFr: string | null;
  descriptionEn: string | null;
  equipmentsFr: string | null;
  equipmentsEn: string | null;
  medias: { url: string; webpUrl: string | null; position: number }[];
  mainImageUrl: string | null;
};
export type PublicStudioModel = components["schemas"]["StudioModel.jsonld-studio_model.read_read"];

// Application write type for form submission
export type ApplicationWrite = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message?: string | null;
  studio?: string | null; // IRI format: "/api/studios/{uuid}"
  locale: string;
  status: string;
  recaptchaToken: string | null;
};

// Contact write type for form submission
export type ContactWrite = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  recaptchaToken: string | null;
};

// Public API without authentication
export const publicApi = createApi({
  reducerPath: "publicApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/",
    prepareHeaders: (headers) => {
      // JSON-LD format for API Platform
      headers.set("Accept", "application/ld+json");
      return headers;
    },
  }),
  tagTypes: ["PublicStudios", "PublicStudioModels"],
  endpoints: (builder) => ({
    // Get all enabled studios (public)
    getPublicStudios: builder.query<ApiResponse<PublicStudio>, Record<string, string | number | boolean | string[] | undefined>>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("enabled", "true");
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== "") {
            if (Array.isArray(value)) {
              value.forEach((v) => searchParams.append(key, v));
            } else {
              searchParams.set(key, String(value));
            }
          }
        });
        return `public_studios?${searchParams.toString()}`;
      },
      providesTags: ["PublicStudios"],
    }),

    // Get single studio by number (public)
    getPublicStudioByNumber: builder.query<PublicStudio | null, number>({
      query: (number) => `public_studios?enabled=true&number=${number}`,
      transformResponse: (response: ApiResponse<PublicStudio>) => {
        return response.member?.[0] ?? null;
      },
      providesTags: (_result, _error, number) => [{ type: "PublicStudios", id: number }],
    }),

    // Get studio models (public) - for studio types catalog
    getPublicStudioModels: builder.query<ApiResponse<PublicStudioModel>, Record<string, string | number | boolean | undefined>>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("enabled", "true");
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== "") {
            searchParams.set(key, String(value));
          }
        });
        return `studio_models?${searchParams.toString()}`;
      },
      providesTags: ["PublicStudioModels"],
    }),

    // Create application (public - no auth required)
    createPublicApplication: builder.mutation<unknown, ApplicationWrite>({
      query: (body) => ({
        url: "applications",
        method: "POST",
        body,
        headers: {
          'X-XSRF-TOKEN': getCsrfToken() ?? undefined,
        },
      }),
    }),

    // Create contact message (public - no auth required)
    createPublicContact: builder.mutation<unknown, ContactWrite>({
      query: (body) => ({
        url: "contacts",
        method: "POST",
        body,
        headers: {
          'X-XSRF-TOKEN': getCsrfToken() ?? undefined,
        },
      }),
    }),

    // Upload de document via token (public, pas d'auth)
    uploadApplicationDocument: builder.mutation<
      unknown,
      { token: string; file: File }
    >({
      query: ({ token, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `applications/${token}/documents`,
          method: 'POST',
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useGetPublicStudiosQuery,
  useGetPublicStudioByNumberQuery,
  useGetPublicStudioModelsQuery,
  useCreatePublicApplicationMutation,
  useCreatePublicContactMutation,
  useUploadApplicationDocumentMutation,
} = publicApi;
